/**
 * @spec docs/specs/features/wallet-relation-api.md
 * Shared types for wallet relations API
 */

// ============================================================================
// EIP-712 Domain and Types
// ============================================================================

/**
 * EIP-712 domain separator for wallet relations signatures.
 * Chain-agnostic (no chainId) for cross-chain compatibility.
 */
export const EIP712_DOMAIN = {
  name: 'PHNM Wallet Relations',
  version: '1',
} as const;

/**
 * EIP-712 type definition for linking two wallets
 */
export const LINK_WALLETS_TYPES = {
  LinkWallets: [
    { name: 'walletA', type: 'address' },
    { name: 'walletB', type: 'address' },
  ],
} as const;

/**
 * EIP-712 type definition for querying wallet relations
 * Includes nonce for replay protection
 */
export const QUERY_RELATIONS_TYPES = {
  QueryRelations: [
    { name: 'wallet', type: 'address' },
    { name: 'timestamp', type: 'uint256' },
    { name: 'nonce', type: 'uint256' },
  ],
} as const;

/**
 * EIP-712 type definition for unlinking a wallet
 * Includes timestamp and nonce for replay protection
 */
export const UNLINK_WALLET_TYPES = {
  UnlinkWallet: [
    { name: 'wallet', type: 'address' },
    { name: 'timestamp', type: 'uint256' },
    { name: 'nonce', type: 'uint256' },
  ],
} as const;

/**
 * EIP-712 type definition for setting a wallet label.
 * Uses timestamp-only replay protection (no nonce) since labels are idempotent.
 */
export const SET_LABEL_TYPES = {
  SetLabel: [
    { name: 'wallet', type: 'address' },
    { name: 'targetWallet', type: 'address' },
    { name: 'label', type: 'string' },
    { name: 'timestamp', type: 'uint256' },
  ],
} as const;

// ============================================================================
// API Request Types
// ============================================================================

/**
 * Request body for POST /relations/link
 */
export interface LinkWalletsRequest {
  walletA: string;
  walletB: string;
  signatureA: string;
  signatureB: string;
}

/**
 * Request body for POST /relations/query
 * Includes nonce for replay protection
 */
export interface QueryRelationsRequest {
  wallet: string;
  timestamp: number;
  nonce: number;
  signature: string;
}

/**
 * Request body for POST /relations/unlink
 * Includes timestamp and nonce for replay protection
 */
export interface UnlinkWalletRequest {
  wallet: string;
  timestamp: number;
  nonce: number;
  signature: string;
}

/**
 * Request body for PUT /relations/label
 * Uses timestamp-only replay protection (idempotent operation)
 */
export interface SetLabelRequest {
  wallet: string;
  targetWallet: string;
  label: string;
  timestamp: number;
  signature: string;
}

/**
 * Response from PUT /relations/label
 */
export interface SetLabelResponse {
  wallet: string;
  targetWallet: string;
  label: string;
}

// ============================================================================
// API Response Types
// ============================================================================

/**
 * A linked wallet entry
 */
export interface LinkedWallet {
  address: string;
  linkedAt: string;
  label?: string;
}

/**
 * Response from POST /relations/link
 */
export interface LinkWalletsResponse {
  relationId: string;
  walletA: string;
  walletB: string;
  createdAt: string;
}

/**
 * Response from POST /relations/query
 */
export interface QueryRelationsResponse {
  wallet: string;
  linkedWallets: LinkedWallet[];
}

/**
 * Response from POST /relations/unlink
 */
export interface UnlinkWalletResponse {
  wallet: string;
  unlinkedCount: number;
  unlinkedAt: string;
}

/**
 * Health check response
 */
export interface HealthResponse {
  status: 'healthy' | 'unhealthy';
  timestamp: string;
  database?: boolean;
}

/**
 * Response from GET /nonce/:wallet
 */
export interface WalletNoncesResponse {
  wallet: string;
  queryNonce: string;
  unlinkNonce: string;
}

/**
 * Error response from API
 */
export interface WalletRelationError {
  error: string;
  code: string;
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Result of canonical address ordering
 */
export interface CanonicalAddresses {
  walletA: string;
  walletB: string;
}
